/**
 * dev-spawn-entity.ts — MFix11.3 — Dev Toolbox instant-spawn helper.
 *
 * Spawns a single ShipInstance at the given world coordinates, owned by
 * the given empire, using the chassis template from the given race's
 * catalogue. Reuses the canonical ship-init pattern from
 * `scenario.ts:spawn` and `advance-construction.ts:spawnBuiltStructure`
 * — both ships and space structures share the `state.shipTemplates`
 * map keyed by `${raceId}:${chassis}`, so a single helper handles
 * both kinds.
 *
 * Race + empire are independent params on purpose: the empire is who
 * OWNS the spawned entity (drives accent colour, FoW vision, fleet
 * accounting); the race is which catalogue the chassis is drawn from
 * (a RACE-AA empire can be tested with a RACE-BB chassis for cross-
 * race scenarios). Normally race === empire's race; the divergence is
 * a dev-only affordance.
 *
 * Strictly dev-only: directly mutates GameState without going through
 * the command stream. Same MULTIPLAYER §2 caveat as the dev-grant
 * helpers (v3M13) — single-player only; v3M9 multiplayer will need a
 * command path with server arbitration.
 *
 * Returns the new ship's id on success, or `null` when the template
 * isn't present in the catalogue (most likely cause: caller passed a
 * race/chassis combination that doesn't exist — UI should filter this
 * at the entity-picker level but the helper stays defensive).
 */

import { initWeaponSlots } from './scenario';
import { effectiveMaxHull, effectiveMaxShields } from './research-effects';
import {
  MINEFIELD_CHASSIS,
  mineCountInitial,
} from './balance-space-structures';
import {
  AI_CARRIER_DEFAULTS_BY_BEHAVIOUR,
  CARRIER_SQUADRON_CAPACITY,
  DEFAULT_CARRIER_ATTACK_BEHAVIOUR,
  DEFAULT_CARRIER_DEFENSE_BEHAVIOUR,
  isCarrierTemplate,
} from './balance-carrier-squadrons';
import {
  allocEntityId,
  type EmpireId,
  type GameState,
  type RaceId,
  type ShipInstance,
  type ShipInstanceId,
  type SystemId,
} from '../state/types/index';

export interface DevSpawnEntityParams {
  /** Empire that will own the spawned entity. */
  ownerEmpireId: EmpireId;
  /** Race catalogue to draw the chassis template from. */
  raceId: RaceId;
  /** Chassis name (matches the catalogue's chassis field for the race). */
  chassis: string;
  /** In-system local coords (0..1) — typically from a tactical-view click. */
  sx: number;
  sy: number;
  /** System the entity spawns into. */
  systemId: SystemId;
}

/**
 * Spawn one entity per the params. Returns the new ship id on success.
 * Returns `null` when:
 *   - `ownerEmpireId` doesn't match a live empire
 *   - `raceId:chassis` template isn't in `state.shipTemplates`
 *
 * Caller is expected to have validated both at the UI level; the
 * defensive returns just keep the dev tool failure-tolerant.
 */
export function devSpawnEntity(
  state: GameState,
  params: DevSpawnEntityParams,
): ShipInstanceId | null {
  const { ownerEmpireId, raceId, chassis, sx, sy, systemId } = params;
  const empire = state.empires.get(ownerEmpireId);
  if (!empire) return null;
  const tmpl = state.shipTemplates.get(`${raceId}:${chassis}`);
  if (!tmpl) return null;

  // MFix42.3 (2026-06-01) — cross-race dev-spawn template alias.
  //
  // The rest of the engine looks up ship templates via the owner
  // empire's race key (advance-detection.ts:templateFor,
  // scanners.ts:collectTeamScannersForSystem,
  // advance-discovery.ts:shipEffectiveScanRange, system-view.ts
  // vision/scan rings). When the dev-spawn uses a different race
  // catalogue than the owner empire (RACE-AA empire owning a RACE-BB
  // chassis), those lookups miss — Vision/Scan/all template-derived
  // stats collapse to 0 because tmpl is undefined.
  //
  // Fix: when raceId differs from empire.raceId, alias the source
  // template under the owner empire's catalogue key so the standard
  // lookup pattern resolves. The aliased template is the same object,
  // so any in-flight mutations stay consistent.
  //
  // Caveat: shipTemplates is rebuilt from data files on save/load, so
  // the alias doesn't survive a round-trip. Cross-race dev-spawned
  // ships saved + loaded will re-trigger the missing-template bug.
  // Workaround for now: re-spawn after load. Long-term fix would store
  // ship.chassisRaceId on the instance and consult it before
  // empire.raceId in all lookups (save-format-affecting).
  if (raceId !== empire.raceId) {
    const aliasKey = `${empire.raceId}:${chassis}`;
    if (!state.shipTemplates.has(aliasKey)) {
      // state.shipTemplates is typed ReadonlyMap (the catalogue is
      // logically immutable post-load); the dev-spawn alias is the
      // narrow exception, so cast to Map for the targeted .set call.
      // Same cast pattern used by dev-spawn-entity.test.ts fixtures.
      (state.shipTemplates as Map<string, typeof tmpl>).set(aliasKey, tmpl);
    }
  }

  const ship: ShipInstance = {
    id: allocEntityId(state),
    chassis,
    ownerEmpireId,
    currentHull: effectiveMaxHull(state, empire, tmpl),
    currentShields: effectiveMaxShields(state, empire, tmpl),
    currentEnergy: tmpl.energyCapacity ?? 0,
    disabledTicks: 0,
    cloaked: false,
    systemId,
    sx,
    sy,
    moveOrder: null,
    jumpOriginSystemId: null,
    jumpTargetSystemId: null,
    jumpArrivalAngle: null,
    jumpTargetGateId: null,
    pendingWarpGateLinkId: null,
    jumpTicksRemaining: 0,
    chargeUpTicksRemaining: 0,
    jumpFuelCostPaid: 0,
    jumpDurationTotal: 0,
    lastFiredTick: null,
    lastFiredTargetShipId: null,
    pendingJumpSystemId: null,
    pendingJumpArrivalAngle: null,
    pendingColoniseTargetId: null,
    constructionOrder: null,
    bombardOrder: null,
    weaponSlots: initWeaponSlots(
      tmpl.weaponLoadout,
      state.weapons,
      empire.researchedTechs,
    ),
    commandKind: 'idle',
    waypoints: [],
    guardTargetKind: null,
    guardTargetId: null,
  };

  // Mirror advance-construction.ts:spawnBuiltStructure — Minefield
  // chassis needs its mine-count + cloak-reveal + trigger-list seeded
  // at spawn time. Non-Minefield entities leave these undefined.
  if (chassis === MINEFIELD_CHASSIS) {
    ship.mineCount = mineCountInitial(state.structuresData);
    ship.mineCloakRevealed = false;
    ship.mineLastTriggerTickByShipId = [];
  }

  // MFix13.3d — Mirror advance-shipyard.ts:spawnBuiltShip — carriers
  // need their embarked-squadron state seeded at spawn time. Without
  // this, `embarkedSquadronCount` stays `undefined`, which is the
  // sentinel the rebuild + auto-launch passes in
  // `advance-carrier-squadrons.ts` use to mean "not a carrier" — so
  // dev-spawned carriers never rebuild fighters AND the detail-panel
  // Launch Fighters button stays hidden (it gates on
  // `embarkedSquadronCount > 0`). Use the same full-dock initial
  // condition as the shipyard path.
  if (isCarrierTemplate(tmpl)) {
    ship.embarkedSquadronCount = CARRIER_SQUADRON_CAPACITY;
    ship.rebuildProgressFighters = 0;
    if (empire.isPlayer) {
      ship.carrierAttackBehaviour = DEFAULT_CARRIER_ATTACK_BEHAVIOUR;
      ship.carrierDefenseBehaviour = DEFAULT_CARRIER_DEFENSE_BEHAVIOUR;
    } else {
      const ai = AI_CARRIER_DEFAULTS_BY_BEHAVIOUR[empire.aiArchetype];
      ship.carrierAttackBehaviour = ai.attack;
      ship.carrierDefenseBehaviour = ai.defense;
    }
  }

  state.ships.set(ship.id, ship);
  empire.shipIds.push(ship.id);
  return ship.id;
}
